<template>

    <Head title="Fixture Types"/>

    <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">Fixture Types</h1>

    <SettingsTabs/>

    <div class="bg-white py-6 px-4 sm:p-6 shadow">
        <div class="flex justify-end">
            <search-filter :show-filter="false" v-model:value="form.search" class="w-1/4 max-w-md mr-4" @reset="reset"></search-filter>
            <Button v-on:click="create"
                    class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral">
                <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Add New Fixture Type
            </Button>
        </div>
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Unit 4 Ref
                                </th>
                                <th scope="col" class="relative px-6 py-3">
                                    <span class="sr-only">Edit</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="fixture_types.data.length > 0" v-for="(fixture_type, index) in fixture_types.data" :key="index"
                                :class="{'bg-gray-100': index % 2}" class="hover:bg-gray-50 cursor-pointer">
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="edit(fixture_type)">
                                    {{ fixture_type.name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" @click="edit(fixture_type)">
                                    {{ fixture_type.unit4_ref }}
                                </td>
                                <td class="px-6  py-4 whitespace-nowrap text-right text-sm">
                                    <span class="text-red-600 hover:text-red-900 float-right"
                                          @click="deleteFixtureType(fixture_type)">
                                        <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none"
                                             viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                        </svg>
                                    </span>
                                </td>
                            </tr>
                            <tr v-else>
                                <td colspan="6" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">
                                    There are no fixture types available for your search criteria.
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="fixture_types.links"/>
        </div>
    </div>
    <CreateFixtureType ref="slideout"/>
</template>


<script>

import {Head} from "@inertiajs/vue3";
import SettingsTabs from "@/Components/SettingsTabs";
import Authenticated from "@/Layouts/Authenticated";
import CreateFixtureType from "@/Components/SlideOuts/CreateFixtureType";
import { router } from '@inertiajs/vue3';
import Pagination from "@/Components/Pagination";
import SearchFilter from "@/Components/SearchFilter";
import throttle from 'lodash/throttle'
import debounce from 'lodash/debounce'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import { PlusSmIcon as PlusSmIconSolid } from '@heroicons/vue/solid'
import Button from "@/Components/Button";


export default {
    layout: Authenticated,
    components: {
        Button,
        Pagination,
        SettingsTabs,
        Head,
        CreateFixtureType,
        SearchFilter,
        PlusSmIconSolid
    },
    props: {
        fixture_types: Object
    },
    data() {
        return {
            form: {
                search: null
            }
        }
    },
    methods: {
        create() {
            this.$refs.slideout.updating = false;
            this.$refs.slideout.open();
        },
        edit(fixture_type) {
            this.$refs.slideout.updating = true;
            this.$refs.slideout.form.id = fixture_type.id;
            this.$refs.slideout.form.name = fixture_type.name;
            this.$refs.slideout.form.unit4_ref = fixture_type.unit4_ref;
            this.$refs.slideout.open();
        },
        deleteFixtureType(id) {
            this.$swal.fire({
                title: 'Remove Fixture Type',
                html: "You are removing a fixture type. <br/>Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Delete the country
                        this.$inertia.delete(route('admin.settings.fixture-types.fixture-type.delete', id), {
                            preserveScroll: true
                        });
                    }
                });
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        }
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function() {
                this.$inertia.get(route('admin.settings.fixture-types.index'), pickBy(this.form), { preserveState: true })
            }, 500),
        },
    },

}
</script>
